import { apiClient } from './apiClient';
import type { SupportInboundMessageListResult } from './supportInboundApi';

export type ServiceTicketComment = {
  id: string;
  serviceTicketId: string;
  body: string;
  isInternal: boolean;
  isHtml?: boolean;
  createdByUserId: string;
  createdByUserName?: string | null;
  createdAtUtc: string;
};

export type ServiceTicketAttachment = {
  id: string;
  serviceTicketId: string;
  serviceTicketCommentId?: string | null;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  fileExtension?: string | null;
  uploadedByUserId: string;
  uploadedByUserName?: string | null;
  createdAtUtc: string;
};

export type ServiceTicketAttachmentDownload = {
  attachmentId: string;
  fileName: string;
  url: string;
  expiresAtUtc: string;
};

export type ServiceTicketEvent = {
  id: string;
  eventType: string;
  description: string;
  oldValue?: string | null;
  newValue?: string | null;
  createdByUserId: string;
  createdByUserName?: string | null;
  createdAtUtc: string;
};

export type ServiceTicketSummary = {
  id: string;
  ticketNumber: number;
  ticketCode: string;
  ticketType: string;
  title: string;
  companyId: string;
  companyName: string;
  contactId?: string | null;
  contactName?: string | null;
  assignedTeamId?: string | null;
  assignedTeamName?: string | null;
  assignedToUserId?: string | null;
  assignedToUserName?: string | null;
  slaId?: string | null;
  slaName?: string | null;
  serviceCatalogItemId?: string | null;
  serviceCatalogItemName?: string | null;
  tags?: string | null;
  status: string;
  priority: string;
  impact?: string | null;
  urgency?: string | null;
  parentTicketId?: string | null;
  parentTicketCode?: string | null;
  contractId?: string | null;
  contractCode?: string | null;
  contractTitle?: string | null;
  problemId?: string | null;
  problemCode?: string | null;
  problemTitle?: string | null;
  salesProductId?: string | null;
  salesProductName?: string | null;
  sourceChannel: string;
  supportChannelConfigId?: string | null;
  sourceChannelName?: string | null;
  assistantConversationId?: string | null;
  assistantHandoffAtUtc?: string | null;
  conversationOwner?: string;
  conversationOwnerLabel?: string;
  alertOccurrenceCount?: number;
  slaResponseDueAtUtc?: string | null;
  slaResolutionDueAtUtc?: string | null;
  firstResponseAtUtc?: string | null;
  resolvedAtUtc?: string | null;
  closedAtUtc?: string | null;
  sortOrder: number;
  createdByUserId: string;
  createdByUserName?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type ServiceTicket = ServiceTicketSummary & {
  description?: string | null;
  formDataJson?: string | null;
  externalConversationId?: string | null;
  comments: ServiceTicketComment[];
  attachments: ServiceTicketAttachment[];
  events: ServiceTicketEvent[];
};

export type ServiceTicketPipelineStage = {
  status: string;
  count: number;
};

export type ServiceTicketPipeline = {
  stages: ServiceTicketPipelineStage[];
  totalCount: number;
};

export type ServiceTicketListResult = {
  pipeline?: ServiceTicketPipeline | null;
  items: ServiceTicketSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type ServiceTicketHistoryListResult = {
  items: ServiceTicketSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type ServiceTicketListOptions = {
  includeArchived?: boolean;
  includePipeline?: boolean;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
};

export type ServiceTicketHistoryOptions = {
  search?: string;
  companyId?: string;
  updatedFromUtc?: string;
  updatedToUtc?: string;
  page?: number;
  pageSize?: number;
};

export type ServiceTicketUpsertPayload = {
  title: string;
  description?: string;
  ticketType?: string;
  companyId: string;
  contactId?: string;
  assignedTeamId?: string;
  assignedToUserId?: string;
  slaId?: string;
  serviceCatalogItemId?: string;
  formDataJson?: string;
  status?: string;
  priority?: string;
  impact?: string;
  urgency?: string;
  parentTicketId?: string;
  contractId?: string;
  problemId?: string;
  salesProductId?: string;
  sourceChannel?: string;
};

export type ServiceTicketStatusUpdatePayload = {
  status: string;
  sortOrder?: number;
};

export type ServiceTicketCommentPayload = {
  body: string;
  isInternal?: boolean;
};

export type ServiceTicketTeamMember = {
  id: string;
  name: string;
  email: string;
};

export type ServiceTicketTeam = {
  id: string;
  name: string;
};

export type ServiceTicketSlaOption = {
  id: string;
  name: string;
  timeInMinutes: number;
  countType: string;
};

export async function getServiceTicketPipeline(search?: string): Promise<ServiceTicketPipeline> {
  const response = await apiClient.get<ServiceTicketPipeline>('/v1/customer-success/tickets/pipeline', {
    params: { search: search?.trim() || undefined },
  });
  return response.data;
}

export async function listServiceTicketHistory(
  options?: ServiceTicketHistoryOptions,
): Promise<ServiceTicketHistoryListResult> {
  const response = await apiClient.get<ServiceTicketHistoryListResult>(
    '/v1/customer-success/tickets/history',
    {
      params: {
        search: options?.search?.trim() || undefined,
        companyId: options?.companyId || undefined,
        updatedFromUtc: options?.updatedFromUtc || undefined,
        updatedToUtc: options?.updatedToUtc || undefined,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? 25,
      },
    },
  );
  return response.data;
}

export async function listServiceTickets(
  options?: ServiceTicketListOptions,
): Promise<ServiceTicketListResult> {
  const response = await apiClient.get<ServiceTicketListResult>('/v1/customer-success/tickets', {
    params: {
      includeArchived: options?.includeArchived ?? false,
      includePipeline: options?.includePipeline ?? false,
      status: options?.status || undefined,
      search: options?.search?.trim() || undefined,
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 50,
    },
  });
  return response.data;
}

export async function getServiceTicket(id: string): Promise<ServiceTicket> {
  const response = await apiClient.get<ServiceTicket>(`/v1/customer-success/tickets/${id}`);
  return response.data;
}

export async function createServiceTicket(payload: ServiceTicketUpsertPayload): Promise<ServiceTicket> {
  const response = await apiClient.post<ServiceTicket>('/v1/customer-success/tickets', payload);
  return response.data;
}

export async function updateServiceTicket(
  id: string,
  payload: ServiceTicketUpsertPayload,
): Promise<ServiceTicket> {
  const response = await apiClient.put<ServiceTicket>(`/v1/customer-success/tickets/${id}`, payload);
  return response.data;
}

export async function updateServiceTicketStatus(
  id: string,
  payload: ServiceTicketStatusUpdatePayload,
): Promise<ServiceTicket> {
  const response = await apiClient.patch<ServiceTicket>(
    `/v1/customer-success/tickets/${id}/status`,
    payload,
  );
  return response.data;
}

export async function addServiceTicketComment(
  id: string,
  payload: ServiceTicketCommentPayload,
): Promise<ServiceTicketComment> {
  const response = await apiClient.post<ServiceTicketComment>(
    `/v1/customer-success/tickets/${id}/comments`,
    payload,
  );
  return response.data;
}

export type TicketSuggestPurpose = 'Reply' | 'InternalNote';

export async function suggestServiceTicketMessage(
  id: string,
  purpose: TicketSuggestPurpose,
  hint?: string,
): Promise<{ text: string; purpose: string }> {
  const response = await apiClient.post<{ text: string; purpose: string }>(
    `/v1/customer-success/tickets/${id}/suggest-message`,
    {
      purpose,
      hint: hint?.trim() || undefined,
    },
  );
  return response.data;
}

export async function listTicketTeamMembers(): Promise<ServiceTicketTeamMember[]> {
  const response = await apiClient.get<ServiceTicketTeamMember[]>(
    '/v1/customer-success/tickets/team-members',
  );
  return response.data;
}

export async function listTicketTeams(): Promise<ServiceTicketTeam[]> {
  const response = await apiClient.get<ServiceTicketTeam[]>('/v1/customer-success/tickets/teams');
  return response.data;
}

export async function listTicketSlaOptions(): Promise<ServiceTicketSlaOption[]> {
  const response = await apiClient.get<ServiceTicketSlaOption[]>(
    '/v1/customer-success/tickets/sla-options',
  );
  return response.data;
}

export async function listServiceTicketAttachments(ticketId: string): Promise<ServiceTicketAttachment[]> {
  const response = await apiClient.get<ServiceTicketAttachment[]>(
    `/v1/customer-success/tickets/${ticketId}/attachments`,
  );
  return response.data;
}

export async function uploadServiceTicketAttachment(
  ticketId: string,
  file: File,
  commentId?: string,
): Promise<ServiceTicketAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  if (commentId) {
    formData.append('commentId', commentId);
  }

  const response = await apiClient.post<ServiceTicketAttachment>(
    `/v1/customer-success/tickets/${ticketId}/attachments`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function getServiceTicketAttachmentDownloadUrl(
  ticketId: string,
  attachmentId: string,
): Promise<ServiceTicketAttachmentDownload> {
  const response = await apiClient.get<ServiceTicketAttachmentDownload>(
    `/v1/customer-success/tickets/${ticketId}/attachments/${attachmentId}/download-url`,
  );
  return response.data;
}

export async function deleteServiceTicketAttachment(
  ticketId: string,
  attachmentId: string,
): Promise<void> {
  await apiClient.delete(`/v1/customer-success/tickets/${ticketId}/attachments/${attachmentId}`);
}

export async function listTicketInboundMessages(ticketId: string): Promise<SupportInboundMessageListResult> {
  const response = await apiClient.get<SupportInboundMessageListResult>(
    `/v1/customer-success/tickets/${ticketId}/inbound-messages`,
  );
  return response.data;
}
